// Sugalarm — delete-confirmation modal (cycle 42).
//
// Movie-night feedback: a misplaced tap on the `×` of a recent-entry
// row silently wiped a real bolus, and the IOB ring vanished with no
// way to tell what was lost. Cycle 42 gates every treatment delete
// behind this modal so the user reads back exactly what they're about
// to remove before it's gone.
//
// Same animation pattern as the cycle-34.20 EditTreatmentModal /
// cycle-34.22 ViewEntryModal: fixed blurred backdrop, 220ms scale-in,
// tap-outside + Escape dismiss. The confirm button is destructive
// (red `var(--zone-urgent)`, white text); Cancel is neutral.
//
// Wired into both delete call sites — LogScreen and the Recent
// Entries drill-down — so every variant picks it up. The cycle-34.20
// pencil edit button is untouched; this gates delete only.

import { useEffect, useState } from "react";

import { fmtTime } from "../../lib/glucose";
import type { Treatment } from "../../lib/types";

interface Props {
  treatment: Treatment | null;
  onClose: () => void;
  onConfirm: () => void;
  /** 24-hour clock preference. Falls back to 12-hour. */
  h24?: boolean;
}

/** Per-kind read-back sentence so the user confirms the specific row,
 *  not a generic "are you sure". BG-check and exercise values live in
 *  `notes` (no dedicated column) so they're parsed back out; the
 *  parse falls back gracefully if the format ever differs. */
function describe(t: Treatment, h24: boolean): string {
  const at = `logged at ${fmtTime(t.timestamp, { h24 })}`;
  const notes = (t.notes ?? "").trim();
  switch (t.event_type) {
    case "insulin": {
      const u = t.insulin_u ?? 0;
      return `Remove ${u}u of insulin ${at}?`;
    }
    case "carbs": {
      const g = t.carbs_g ?? 0;
      return `Remove ${g}g of carbs${notes ? ` ("${notes}")` : ""} ${at}?`;
    }
    case "bg_check": {
      const m = notes.match(/^BG\s+([\d.]+)\s+(mg\/dL|mmol\/L)/i);
      return m
        ? `Remove BG check of ${m[1]} ${m[2]} ${at}?`
        : `Remove BG check ${at}?`;
    }
    case "exercise": {
      const m = notes.match(/^([\d.]+)\s*min/i);
      return m
        ? `Remove ${m[1]} min of exercise ${at}?`
        : `Remove exercise ${at}?`;
    }
    case "note":
      return `Remove note ${at}?`;
    default:
      return `Remove this entry ${at}?`;
  }
}

export function ConfirmDeleteModal({
  treatment,
  onClose,
  onConfirm,
  h24 = false,
}: Props) {
  const [mounted, setMounted] = useState<boolean>(treatment != null);
  const visible = treatment != null;

  useEffect(() => {
    if (visible) {
      setMounted(true);
      return;
    }
    const t = window.setTimeout(() => setMounted(false), 220);
    return () => window.clearTimeout(t);
  }, [visible]);

  // Escape dismisses on kiosk + web where a physical keyboard exists.
  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [visible, onClose]);

  if (!mounted || !treatment) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Confirm delete"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 365,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: visible ? "rgba(0, 0, 0, 0.32)" : "transparent",
        backdropFilter: "blur(3px)",
        transition: "background 220ms ease-out",
        pointerEvents: visible ? "auto" : "none",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(420px, 100%)",
          padding: "22px 22px 18px",
          background: "var(--bg-elev)",
          border: "0.5px solid var(--line-strong)",
          borderRadius: 18,
          boxShadow: "0 16px 48px rgba(0, 0, 0, 0.4)",
          transform: visible
            ? "scale(1) translateY(0)"
            : "scale(0.94) translateY(8px)",
          opacity: visible ? 1 : 0,
          transition: "transform 220ms ease-out, opacity 200ms ease-out",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <div
          style={{
            fontSize: "var(--type-floor)",
            fontWeight: 700,
            letterSpacing: "0.10em",
            textTransform: "uppercase",
            color: "var(--zone-urgent)",
          }}
        >
          Remove entry
        </div>

        <div
          style={{
            fontSize: 15,
            color: "var(--ink)",
            lineHeight: 1.45,
          }}
        >
          {describe(treatment, h24)}
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 2 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              appearance: "none",
              flex: 1,
              border: "0.5px solid var(--line-strong)",
              background: "transparent",
              color: "var(--ink-2)",
              borderRadius: 10,
              padding: "10px 14px",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            style={{
              appearance: "none",
              flex: 1,
              border: 0,
              background: "var(--zone-urgent)",
              color: "#fff",
              borderRadius: 10,
              padding: "10px 14px",
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  );
}
